/**
 * O'Copro - ErrorBoundary
 * Composant React class qui capture les erreurs JavaScript dans l'arbre
 * de composants enfants et affiche un fallback UI au lieu d'un écran blanc.
 * 
 * Usage dans App.tsx :
 *   <ErrorBoundary>
 *     <Routes>...</Routes>
 *   </ErrorBoundary>
 * 
 * src/components/common/ErrorBoundary.tsx
 */

import React from 'react';
import { AlertTriangle, RefreshCw, Home, ChevronDown, ChevronUp } from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  showDetails: boolean;
  isChunkError: boolean;
}

// ============================================================================
// COMPOSANT
// ============================================================================

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      isChunkError: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const msg = error?.message ?? '';
    const isChunkError =
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed');
    return { hasError: true, error, isChunkError };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const msg = error?.message ?? '';
    const isChunkError =
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed');

    if (isChunkError) {
      const reloadKey = 'chunk_reload_ts';
      const last = Number(sessionStorage.getItem(reloadKey) ?? 0);
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(reloadKey, String(Date.now()));
        window.location.reload();
      }
      return;
    }

    this.setState({ errorInfo });
    console.error('[ErrorBoundary] Erreur capturée:', error);
    console.error('[ErrorBoundary] Stack composant:', errorInfo.componentStack);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    // Reset l'état puis navigue
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  handleToggleDetails = (): void => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.state.isChunkError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <p className="text-gray-400 text-sm">Mise à jour en cours…</p>
        </div>
      );
    }

    const { error, errorInfo, showDetails } = this.state;

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          {/* Card principale */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Header émeraude */}
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 px-6 py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={32} className="text-white" />
              </div>
              <h1 className="text-xl font-bold text-white">
                Une erreur est survenue
              </h1>
              <p className="text-emerald-100 text-sm mt-2">
                L'application a rencontré un problème inattendu
              </p>
            </div>

            {/* Contenu */}
            <div className="p-6 space-y-4">
              <p className="text-gray-600 text-sm text-center leading-relaxed">
                Nous nous excusons pour la gêne occasionnée.
                Essayez de recharger la page ou de retourner à l'accueil.
              </p>

              {/* Boutons d'action */}
              <div className="flex gap-3">
                <button
                  onClick={this.handleReload}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors"
                >
                  <RefreshCw size={18} />
                  Recharger
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="flex-1 flex items-center justify-center gap-2 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  <Home size={18} />
                  Accueil
                </button>
              </div>

              {/* Détails de l'erreur (toggle) */}
              <button
                onClick={this.handleToggleDetails}
                className="w-full flex items-center justify-center gap-1 text-gray-400 text-xs hover:text-gray-600 transition-colors pt-2"
              >
                Détails techniques
                {showDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>

              {showDetails && (
                <div className="bg-gray-50 rounded-xl p-4 text-xs font-mono text-gray-500 max-h-48 overflow-y-auto border border-gray-200">
                  <p className="text-red-600 font-semibold mb-2">
                    {error?.name}: {error?.message}
                  </p>
                  {errorInfo?.componentStack && (
                    <pre className="whitespace-pre-wrap text-gray-400 leading-relaxed">
                      {errorInfo.componentStack.trim()}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-gray-400 text-xs mt-4">
            O'Copro v1.0.0
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
